/*!
VueLayers
Vue 2 components to work with OpenLayers

@package vuelayers
@author Vladimir Vershinin <ghettovoice@gmail.com>
@version 0.9.1
@license MIT
@copyright (c) 2017-2018, Vladimir Vershinin <ghettovoice@gmail.com>
*/
import _Object$defineProperties from 'babel-runtime/core-js/object/define-properties';
import _Object$create from 'babel-runtime/core-js/object/create';
import _merge from 'lodash/fp/merge';
import _isEqual from 'lodash/fp/isEqual';
import Feature from 'ol/feature';
import { Observable as Observable$1 } from 'rxjs/Observable';
import { merge as merge$1 } from 'rxjs/observable/merge';
import { distinctUntilChanged as distinctUntilChanged$1 } from 'rxjs/operator/distinctUntilChanged';
import { map as map$1 } from 'rxjs/operator/map';
import { mergeAll as mergeAll$1 } from 'rxjs/operator/mergeAll';
import { throttleTime as throttleTime$1 } from 'rxjs/operator/throttleTime';
import uuid from 'uuid/v4';
import { assert, geomHelper, geometryContainer, mergeDescriptors, observableFromOlEvent, olCmp, plainProps, projTransforms, stylesContainer, useMapCmp } from '../core';

/**
 * @module feature/feature
 */

var mergeNArg = _merge.convert({ fixed: false });

/**
 * @vueProps
 */
var props = /** @lends module:feature/feature# */{
  /**
   * Feature identifier.
   * @type {string|number}
   * @default UUID
   * @vueSync
   */
  id: {
    type: [String, Number],
    default: function _default() {
      return uuid();
    }
  },
  /**
   * All feature properties.
   * @type {Object}
   * @default {}
   * @vueSync
   */
  properties: {
    type: Object,
    default: function _default() {
      return _Object$create(null);
    }
  }

  /**
   * @vueComputed
   */
};var computed = /** @lends module:feature/feature# */{
  /**
   * **GeoJSON** encoded geometry.
   * @type {GeoJSONFeature|undefined}
   */
  geometry: function geometry() {
    if (this.rev && this.$geometry) {
      return this.writeGeometryInBindProj(this.$geometry);
    }
  },

  /**
   * @return {number[]|undefined}
   */
  geometryPoint: function geometryPoint() {
    if (this.rev && this.$geometry) {
      return this.pointToBindProj(geomHelper.pointOnSurface(this.$geometry));
    }
  }
};

/**
 * @vueMethods
 */
var methods = /** @lends module:feature/feature# */{
  /**
   * Create feature without inner style applying, feature level style
   * will be applied in the layer level style function.
   * @return {ol.Feature}
   * @protected
   */
  createOlObject: function createOlObject() {
    var feature = new Feature(this.properties);
    feature.setId(this.id);
    feature.setGeometry(this.$geometry);

    return feature;
  },

  /**
   * @return {{
   *     getGeometry: function(): (ol.geom.Geometry|undefined),
   *     setGeometry: function((ol.geom.Geometry|undefined)): void
   *   }|ol.Feature|undefined}
   * @protected
   */
  getGeometryTarget: function getGeometryTarget() {
    return this.$feature;
  },

  /**
   * @return {Object}
   * @protected
   */
  getServices: function getServices() {
    var vm = this;

    return mergeDescriptors(olCmp.methods.getServices.call(this), geometryContainer.methods.getServices.call(this), stylesContainer.methods.getServices.call(this), {
      get feature() {
        return vm.$feature;
      }
    });
  },

  /**
   * @return {ol.Feature|undefined}
   * @protected
   */
  getStyleTarget: function getStyleTarget() {
    return this.$feature;
  },

  /**
   * Checks if feature lies at `pixel`.
   * @param {number} pixel
   * @return {boolean}
   */
  isAtPixel: function isAtPixel(pixel) {
    var _this = this;

    assert.hasMap(this);

    return this.$map.forEachFeatureAtPixel(pixel, function (f) {
      return f === _this.$feature;
    }, { layerFilter: function layerFilter(l) {
        return l === _this.$layer;
      } });
  },

  /**
   * @return {void}
   * @protected
   */
  mount: function mount() {
    this.$featuresContainer && this.$featuresContainer.addFeature(this);
    this.subscribeAll();
  },

  /**
   * @return {void}
   * @protected
   */
  unmount: function unmount() {
    this.unsubscribeAll();
    this.$featuresContainer && this.$featuresContainer.removeFeature(this);
  },

  /**
   * @return {void}
   * @protected
   */
  subscribeAll: function subscribeAll() {
    subscribeToFeatureChanges.call(this);
  }
};

var watch = {
  /**
   * @param {string|number} value
   */
  id: function id(value) {
    if (this.$feature && value !== this.$feature.getId()) {
      this.$feature.setId(value);
    }
  },

  /**
   * @param {Object} value
   */
  properties: function properties(value) {
    value = plainProps(value);
    if (this.$feature && !_isEqual(value, plainProps(this.$feature.getProperties()))) {
      this.$feature.setProperties(plainProps(value));
    }
  }
};

/**
 * A vector object for geographic features with a geometry and other attribute properties,
 * similar to the features in vector file formats like **GeoJSON**.
 *
 * @title vl-feature
 * @alias module:feature/feature
 * @vueProto
 *
 * @vueSlot default [scoped] Default **scoped** slot with current feature state: `id`, `properties`, GeoJSON `geometry`.
 */
var Feature$1 = { render: function render() {
    var _vm = this;var _h = _vm.$createElement;var _c = _vm._self._c || _h;return _c('i', { class: [_vm.$options.name], staticStyle: { "display": "none !important" }, attrs: { "id": [_vm.$options.name, _vm.id].join('-') } }, [_vm._t("default", null, { id: _vm.id, properties: _vm.properties, geometry: _vm.geometry, geometryPoint: _vm.geometryPoint })], 2);
  }, staticRenderFns: [],
  name: 'vl-feature',
  mixins: [olCmp, useMapCmp, geometryContainer, stylesContainer, projTransforms],
  props: props,
  computed: computed,
  methods: methods,
  watch: watch,
  created: function created() {
    var _this2 = this;

    _Object$defineProperties(this, /** @lends module:feature/feature# */{
      /**
       * Reference to `ol.Feature` instance.
       * @type {ol.Feature|undefined}
       */
      $feature: {
        enumerable: true,
        get: function get() {
          return _this2.$olObject;
        }
      },
      /**
       * Reference to parent `ol.Layer` instance.
       * @type {ol.layer.Layer|undefined}
       */
      $layer: {
        enumerable: true,
        get: function get() {
          return _this2.$services && _this2.$services.layer;
        }
      },
      /**
       * Reference to `ol.Map` instance.
       * @type {ol.Map|undefined}
       */
      $map: {
        enumerable: true,
        get: function get() {
          return _this2.$services && _this2.$services.map;
        }
      },
      /**
       * Reference to `ol.View` instance.
       * @type {ol.View|undefined}
       */
      $view: {
        enumerable: true,
        get: function get() {
          return _this2.$services && _this2.$services.view;
        }
      },
      /**
       * Reference to `featuresContainer`.
       * @type {Object|undefined}
       */
      $featuresContainer: {
        enumerable: true,
        get: function get() {
          return _this2.$services && _this2.$services.featuresContainer;
        }
      }
    });
  }
};

/**
 * @return {void}
 * @private
 */
function subscribeToFeatureChanges() {
  var _this3 = this,
      _context;

  assert.hasFeature(this);

  var getPropValue = function getPropValue(prop) {
    return _this3.$feature.get(prop);
  };
  var ft = 100;
  // all plain properties
  var propChanges = (_context = (_context = observableFromOlEvent(this.$feature, 'propertychange', function (_ref) {
    var key = _ref.key;
    return { prop: key, value: getPropValue(key) };
  }), throttleTime$1).call(_context, ft), distinctUntilChanged$1).call(_context, _isEqual);
  // id, style and other generic changes
  var changes = (_context = (_context = (_context = (_context = observableFromOlEvent(this.$feature, 'change'), map$1).call(_context, function () {
    return Observable$1.create(function (obs) {
      if (_this3.$feature.getId() !== _this3.id) {
        obs.next({ prop: 'id', value: _this3.$feature.getId() });
      }
      // todo style?
    });
  }), mergeAll$1).call(_context), throttleTime$1).call(_context, ft), distinctUntilChanged$1).call(_context, _isEqual);
  // all changes
  var allChanges = merge$1.call(Observable$1, propChanges, changes);

  this.subscribeTo(allChanges, function (_ref2) {
    var prop = _ref2.prop,
        value = _ref2.value;

    ++_this3.rev;

    if (prop === 'id') {
      _this3.$emit('update:' + prop, value);
    } else if (prop !== _this3.$feature.getGeometryName()) {
      _this3.$emit('update:properties', mergeNArg({}, _this3.properties, { prop: value }));
    }
  });
}

/**
 * @module feature
 */
/**
 * @alias module:feature
 *
 * @example Install
 * import Vue from 'vue'
 * // import module and styles
 * import { Feature } from 'vuelayers'
 * // or
 * import Feature from 'vuelayers/lib/feature'
 * // import VueLayers styles
 * import 'vuelayers/lib/style.css'
 * // register components
 * Vue.use(Feature)
 */
var index = {
  /**
   * A vector object for geographic features with a geometry and other attribute properties,
   * similar to the features in vector file formats like **GeoJSON**.
   * @alias module:feature/feature
   */
  Feature: Feature$1,
  install: function install(Vue) {
    Vue.component(Feature$1.name, Feature$1);
  }
};

export default index;

//# sourceMappingURL=index.es.js.map



//////////////////
// WEBPACK FOOTER
// ./node_modules/vuelayers/lib/feature/index.es.js
// module id = ZQhC
// module chunks = 0